import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Tooltip, TextBox } from "@etframework/ui.components";
import useTaskKey from "../../hooks/useTaskKey";
import { AutoRolloverContent } from "../PreferencesComponents/SystemPreferencesComponents/AutoRolloverContent";

export const TasktypeInput = forwardRef(
  ({ initialValue, onBlur, id, ...rest }, ref) => {
    const { t } = useTranslation(["CreateBoardModal", "Shared"]);
    const [title, setTitle] = useState(initialValue.title);
    const generatedKey = useTaskKey(title);
    const keyRef = useRef(null);
    const titleRef = useRef(null);
    const autoRolloverRef = useRef(null);

    const keyInputId = id + "_key-input";
    const titleInputId = id + "_title-input";

    const validateKey = useCallback(
      (arg) => {
        const value = arg ?? keyRef.current?.getValue();
        if (!value) {
          keyRef.current?.setError(t("Required"));
          keyRef.current?.setShowError(true);
        } else if (/[\W\d^_]/g.test(value)) {
          keyRef.current?.setError(t("OnlyLetters"));
          keyRef.current?.setShowError(true);
        } else if (value?.length !== 3) {
          keyRef.current?.setError(t("Minimum", { count: 3 }));
          keyRef.current?.setShowError(true);
        } else {
          keyRef.current?.setShowError(false);
          return true;
        }
      },
      [t]
    );

    const validateTitle = useCallback(() => {
      const value = titleRef.current.getValue();
      if (!value) {
        titleRef.current?.setError(t("Required"));
        titleRef.current?.setShowError(true);
      } else if (value[0] === " ") {
        titleRef.current?.setError(t("NoSpace"));
        titleRef.current?.setShowError(true);
      } else {
        return true;
      }
    }, [t]);

    const validate = useCallback(() => {
      return !!(validateKey() && validateTitle());
    }, [validateKey, validateTitle]);

    const getValue = useCallback(
      () => ({
        key: keyRef.current?.getValue?.(),
        title: titleRef.current?.getValue?.(),
        autoRollover: autoRolloverRef.current?.getValue(),
      }),
      []
    );

    useImperativeHandle(
      ref,
      () => ({
        getValue,
        validate,
      }),
      [getValue, validate]
    );

    const IconTooltip = ({ content }) => (
      <Tooltip
        className={"d-inline"}
        content={() => {
          return <p className="p-2 rounded">{content}</p>;
        }}
        position="TopCenter"
        showTipPointer={false}
      >
        <span className="fa-regular fa-info-circle et-darkest-gray ms-1" />
      </Tooltip>
    );

    return (
      <>
        <div
          id={id}
          onBlur={() => onBlur?.({ value: getValue(), inputName: "all" })}
          className={
            "tasktype-row d-flex flex-row align-items-center gap-3 " +
            rest.className
          }
        >
          <TextBox
            ref={titleRef}
            labelText={
              <>
                {t("TaskType")} <IconTooltip content={t("TaskTypeTooltip")} />
              </>
            }
            id={titleInputId}
            errorMessage={t("Required")}
            value={title}
            labelColor={"#000"}
            placeholder={t("TaskTypePlaceholder")}
            autocomplete="off"
            boldLabel={false}
            onBlur={() => titleRef.current.setShowError(false)}
            onChange={(e) => setTitle(e.value ?? "")}
          />
          <TextBox
            ref={keyRef}
            labelText={
              <>
                {t("Key")} <IconTooltip content={t("KeyTooltip")} />
              </>
            }
            id={keyInputId}
            value={initialValue.key}
            placeholder={generatedKey}
            labelColor={"#000"}
            autocomplete="off"
            boldLabel={false}
            htmlAttributes={{ maxlength: "3" }}
            onChange={(e) => {
              if (e.value?.length === 3) validateKey(e.value);
            }}
          />
        </div>
        <div className="mb-3">
          <AutoRolloverContent ref={autoRolloverRef} show={true} />
        </div>
      </>
    );
  }
);
